import React from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { OrderAnalytics } from '../../../types/admin';

interface OrderStatusChartProps {
  data: OrderAnalytics['order_status_distribution'];
  isLoading?: boolean;
}

const OrderStatusChart: React.FC<OrderStatusChartProps> = ({ data, isLoading }) => {
  const theme = useTheme();

  // Status colors mapping
  const statusColors = {
    completed: '#4caf50',
    processing: '#2196f3',
    created: '#ff9800',
    draft: '#9e9e9e',
    cancelled: '#f44336',
    failed: '#d32f2f',
  };

  // Format data for chart
  const chartData = data ? Object.entries(data).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
    color: statusColors[status as keyof typeof statusColors] || theme.palette.grey[500],
  })).filter(item => item.value > 0) : [];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);
      
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 2,
            boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {data.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Count: {data.value.toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Percentage: {percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom label for pie slices
  const renderLabel = ({ value, percent }: any) => {
    if (percent < 5) return ''; // Hide labels for small slices
    return `${(percent * 100).toFixed(0)}%`;
  };

  if (isLoading) {
    return (
      <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Loading chart...</Typography>
      </Box>
    );
  }

  if (!chartData.length) {
    return (
      <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No order data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 300, width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Order Status Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Breakdown of orders by current status
        </Typography>
      </Box>
      
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderLabel}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.color}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            wrapperStyle={{
              fontSize: '12px',
              paddingTop: '10px'
            }}
            formatter={(value, entry: any) => (
              <span style={{ color: entry.color }}>
                {value} ({entry.payload.value})
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default OrderStatusChart; 